import {
    historyKeyOf,
    nextHistoryKeyOf,
    snapshotTopOffset,
    underlayEdgeShadow,
    underlayOffsetX,
    type SnapshotEntry,
    type SnapshotStore,
} from '@/components/common/swipeBackSnapshot';
import { claudeTokens } from '@/styles/claudeTokens';

/**
 * 스와이프 뒤로가기 밑에 "이전 화면"을 까는 DOM 쪽 코드 — 화면을 떠날 때 본문을 복사하고, 끌 때 그 복사본을 고정 레이어로 깐다.
 *
 * 복사 시점: `history.pushState` 직전.
 * react-router v6(BrowserRouter)의 이동(`<Link>`·`navigate`)은 `globalHistory.pushState()` 를 먼저 부르고 그다음 listener 로
 * setState 를 한다(@remix-run/router `createBrowserHistory().push`). 그래서 pushState 가 불리는 순간의 DOM 은 아직 떠나는 화면이고,
 * scrollY 도 ScrollToTopOnNavigate(새 화면 effect)가 맨 위로 올리기 전 값이다. 링크·navigate·리다이렉트 어느 경로든 한 곳에서 잡힌다.
 * 모달용 이력(useBackToClose·FilterPanel)은 같은 key 로 pushState 하므로 key 가 바뀔 때만 복사해 거른다.
 */

export interface Underlay {
    layer: HTMLDivElement;
    track: HTMLDivElement;
    /** 현재 화면 뒤에 붙어 함께 밀려 나가는 불투명한 바탕 판(헤더 아래 ~ 화면 끝) */
    plate: HTMLDivElement;
    node: HTMLElement;
    /** 본문과 함께 옮기는 앱 푸터(폰에서도 본문 아래에 보인다) — 없으면 null */
    footer: HTMLElement | null;
}

/** 복사본에 넣으면 다시 불러오거나(iframe) 재생되거나(video·audio) 비어 보이는(canvas) 요소 — 같은 크기의 빈 상자로 바꾼다 */
const REPLACED_TAGS: string = 'video, iframe, canvas, audio, object, embed';

/** 복사본을 화면에 걸어도 원본과 부딪히지 않게 다듬는다 */
const sanitizeClone = (clone: HTMLElement): void => {
    // 끄는 중이던 인라인 스타일(transform·그림자·위치)은 복사본에 남기지 않는다
    ['transform', 'transition', 'opacity', 'box-shadow', 'position', 'z-index', 'background-color', 'will-change'].forEach((name: string) =>
        clone.style.removeProperty(name),
    );
    clone.removeAttribute('id');
    clone.querySelectorAll<HTMLElement>('[id]').forEach((element: HTMLElement) => element.removeAttribute('id'));
    // 같은 이름의 라디오가 문서에 붙으면 원본 라디오의 선택이 풀린다(같은 라디오 그룹) — 이름을 지운다
    clone.querySelectorAll<HTMLElement>('[name]').forEach((element: HTMLElement) => element.removeAttribute('name'));
    clone.querySelectorAll<HTMLElement>('[autofocus]').forEach((element: HTMLElement) => element.removeAttribute('autofocus'));
    clone.querySelectorAll<HTMLElement>(REPLACED_TAGS).forEach((element: HTMLElement) => {
        const box: HTMLDivElement = document.createElement('div');
        box.className = element.className;
        box.style.cssText = element.style.cssText;
        const width: string | null = element.getAttribute('width');
        const height: string | null = element.getAttribute('height');
        if (width && !box.style.width) box.style.width = /^\d+$/.test(width) ? `${width}px` : width;
        if (height && !box.style.height) box.style.height = /^\d+$/.test(height) ? `${height}px` : height;
        element.replaceWith(box);
    });
};

export interface CaptureResult {
    entry: SnapshotEntry<HTMLElement>;
    /** 복사본 노드 수(요소만) */
    nodeCount: number;
    /** 복사+다듬기에 걸린 시간(ms) */
    durationMs: number;
}

/** 지금 화면의 본문을 복사한다 — 화면을 떠날 때 한 번만 부른다 */
export const captureContent = (root: HTMLElement, key: string): CaptureResult => {
    const startedAt: number = performance.now();
    const clone: HTMLElement = root.cloneNode(true) as HTMLElement;
    sanitizeClone(clone);
    const durationMs: number = performance.now() - startedAt;
    const scrollY: number = window.scrollY;
    // 위치 기준은 본문의 첫 요소 — 첫 요소의 margin-top 이 본문 밖으로 겹쳐(margin collapse) 본문 상자보다 아래에서 시작하는 화면이 있다
    const anchor: Element = root.firstElementChild ?? root;
    return {
        entry: {
            key,
            node: clone,
            scrollY,
            contentTop: anchor.getBoundingClientRect().top + scrollY,
            width: window.innerWidth,
        },
        nodeCount: clone.getElementsByTagName('*').length + 1,
        durationMs,
    };
};

/** 화면을 떠날 때마다(pushState 직전) 본문을 복사해 보관소에 넣는다. 돌려받은 함수로 원래 pushState/replaceState 를 되돌린다 */
export const installSnapshotCapture = (store: SnapshotStore<HTMLElement>, getRoot: () => HTMLElement | null): (() => void) => {
    const history: History = window.history;
    const originalPush: History['pushState'] = history.pushState;
    const originalReplace: History['replaceState'] = history.replaceState;

    const patchedPush: History['pushState'] = function pushState(this: History, data: unknown, unused: string, url?: string | URL | null) {
        try {
            const currentKey: string = historyKeyOf(history.state);
            const nextKey: string | null = nextHistoryKeyOf(data);
            const root: HTMLElement | null = getRoot();
            if (nextKey !== null && nextKey !== currentKey && root) {
                store.save(captureContent(root, currentKey).entry);
                store.link(nextKey, currentKey);
            }
        } catch {
            // 복사에 실패해도 이동은 막지 않는다 — 스와이프 때 빈 배경으로 둔다
        }
        return originalPush.call(this, data, unused, url);
    };

    const patchedReplace: History['replaceState'] = function replaceState(this: History, data: unknown, unused: string, url?: string | URL | null) {
        try {
            const nextKey: string | null = nextHistoryKeyOf(data);
            if (nextKey !== null) store.relink(historyKeyOf(history.state), nextKey);
        } catch {
            // 연결 갱신 실패는 무시한다
        }
        return originalReplace.call(this, data, unused, url);
    };

    history.pushState = patchedPush;
    history.replaceState = patchedReplace;
    return () => {
        if (history.pushState === patchedPush) history.pushState = originalPush;
        if (history.replaceState === patchedReplace) history.replaceState = originalReplace;
    };
};

/**
 * 레이어 위쪽 끝(뷰포트 기준 px).
 *
 * 헤더 바로 아래가 아니라 화면 맨 위(0)에서 시작한다. 헤더(sticky, z-index 1000)가 레이어(z-index 0)보다 위에 그려져
 * 보이는 결과는 "헤더 아래부터"와 같고, 대신 복사본 안의 sticky(목록 탭 줄 `top: 헤더 높이`)가 원래 화면과 같은 높이에 붙는다.
 * 레이어는 overflow:hidden 이라 sticky 의 기준(스크롤 상자)이 되는데, 레이어가 헤더 아래에서 시작하면 탭 줄이 헤더 높이만큼 더 내려가 붙었다.
 *
 * 단, 헤더 아래 선보다 위는 레이어가 보이면 안 된다(clip-path 로 자른다). 웹의 "명치사 앱 받기" 띠(AppInstallBanner)는 헤더 위에 있는
 * 보통 흐름(position 없음) 요소라, 위치가 있는 레이어(fixed, z-index 0)가 그 위에 그려져 띠를 덮었다 — 끄는 동안 띠가 사라지고
 * 그 자리에 복사본이 비쳐 헤더가 내려간 것처럼 보였다.
 */
const LAYER_TOP_PX: number = 0;
/** 바탕 판이 헤더 밑으로 파고드는 길이 — 헤더(48px 이상)보다 작아야 띠·상태 표시줄 자리를 덮지 않는다 */
const PLATE_TUCK_PX: number = 8;

/** 헤더 아래 선(뷰포트 기준) — 헤더가 숨은 화면(폰 글쓰기)이면 0. 띠·상태 표시줄 자리·헤더는 모두 이 선 위에 있다 */
const headerBottom = (): number => {
    const header: HTMLElement | null = document.querySelector('header');
    if (!header) return 0;
    const rect: DOMRect = header.getBoundingClientRect();
    return rect.height > 0 ? Math.max(0, rect.bottom) : 0;
};

/** 지금 화면 밑에 이전 화면 복사본을 깐다. 복사본이 없으면 null — 지금처럼 빈 배경으로 둔다 */
export const mountUnderlay = (store: SnapshotStore<HTMLElement>, content: HTMLElement | null): Underlay | null => {
    if (!content) return null;
    const entry: SnapshotEntry<HTMLElement> | null = store.previousOf(historyKeyOf(window.history.state), window.innerWidth);
    if (!entry) return null;
    const top: number = LAYER_TOP_PX;
    const background: string = appBackground();

    const layer: HTMLDivElement = document.createElement('div');
    layer.setAttribute('aria-hidden', 'true');
    layer.setAttribute('inert', '');
    layer.dataset.swipeBackUnderlay = 'true';
    // 본문(z-index 1 로 올림)보다 아래, 헤더·하단 탭(z-index 1000)보다 아래
    // 원점은 화면 맨 위(sticky 위치 유지), 보이는 영역만 헤더 아래 선부터
    // 헤더 선이 기기 픽셀 중간에 걸리면(예: 105.44px) 그 줄까지 자른다 — 선 위 픽셀은 하나도 바뀌지 않게
    const dpr: number = window.devicePixelRatio || 1;
    const clip: string = `inset(${Math.max(0, Math.ceil((headerBottom() - top) * dpr) / dpr)}px 0 0 0)`;
    layer.style.cssText = `position:fixed;top:${top}px;left:0;right:0;bottom:0;overflow:hidden;z-index:0;pointer-events:none;background-color:${background};-webkit-clip-path:${clip};clip-path:${clip};will-change:transform;transform:translate3d(${underlayOffsetX(0, window.innerWidth)}px, 0, 0);`;

    const track: HTMLDivElement = document.createElement('div');
    track.style.cssText = `position:absolute;left:0;right:0;top:${snapshotTopOffset(entry.contentTop, entry.scrollY, top)}px;`;
    track.appendChild(entry.node);


    layer.appendChild(track);

    // 현재 화면 바탕 판 — 본문은 투명하고, 첫 요소 margin 이 본문 밖으로 겹친 틈(헤더 아래 약 8px)·카드 사이·짧은 화면의 본문 아래가
    // 비어 있어 그대로면 뒤의 복사본이 비쳐 카드가 떠 보였다. 헤더 아래 선부터 화면 끝까지 덮는 판을 본문과 같이 옮긴다.
    // 레이어보다 위(문서 순서상 뒤, 같은 z-index 0), 본문·푸터(z-index 1)보다 아래. 헤더·하단 탭(z-index 1000)은 그 위.
    const plate: HTMLDivElement = document.createElement('div');
    plate.setAttribute('aria-hidden', 'true');
    plate.dataset.swipeBackPlate = 'true';
    // 위쪽 끝은 헤더 밑으로 조금(8px) 파고들게 둔다 — 헤더 선이 기기 픽셀 중간(105.44px)에 걸리면 옮기는 판이 정수 픽셀로 맞춰지며
    // 선 바로 아래 한 줄이 비어 뒤가 비쳤다. 파고든 부분은 헤더(z-index 1000)에 가려 보이지 않는다.
    plate.style.cssText = `position:fixed;top:${Math.max(0, headerBottom() - PLATE_TUCK_PX)}px;left:0;right:0;bottom:0;z-index:0;pointer-events:none;background-color:${background};will-change:transform;`;

    // body 맨 앞에 넣어 문서 순서상 본문보다 먼저 그려지게 한다(본문은 z-index 1 이라 어차피 위)
    document.body.insertBefore(layer, document.body.firstChild);
    layer.after(plate);
    // 레이어 안(절대 위치 = 새 블록 서식 맥락)에서는 첫 요소의 margin 이 밖으로 겹치지 않아 원래보다 그만큼 내려온다 — 첫 요소가
    // 저장할 때 화면에 있던 자리에 오게 한 번 맞춘다(넣은 직후 한 번 재는 것이라 끄는 동안의 비용은 없다)
    const anchor: Element = entry.node.firstElementChild ?? entry.node;
    const drift: number = anchor.getBoundingClientRect().top - (entry.contentTop - entry.scrollY);
    if (Math.abs(drift) >= 0.5) track.style.top = `${parseFloat(track.style.top) - drift}px`;

    // 본문·푸터를 바탕 판 위로 올린다(끄는 동안만). 푸터는 위치가 없는 보통 흐름이라 그대로 두면 판에 가려 사라진다 — 본문과 같이 옮긴다
    const footer: HTMLElement | null = document.querySelector<HTMLElement>('#root footer');
    [content, footer].forEach((element: HTMLElement | null) => {
        if (!element) return;
        element.style.position = 'relative';
        element.style.zIndex = '1';
    });
    return { layer, track, plate, node: entry.node, footer };
};

/**
 * 끄는 위치(value px)에 맞춰 두 장을 옮긴다 — 현재 화면의 바탕 판·푸터는 +value, 이전 화면 레이어는 -0.3·폭·(1-p)(느리게 따라옴).
 * 본문과 같은 transition(시간·곡선)을 그대로 받아 두 장이 같은 시간에 끝난다. 매 프레임 transform·그림자만 바꾼다.
 */
export const paintUnderlay = (underlay: Underlay, value: number, transition: string): void => {
    const transform: string = value > 0 ? `translate3d(${value}px, 0, 0)` : '';
    const transitionValue: string = transition ? `transform ${transition}` : 'none';
    underlay.layer.style.transition = transitionValue;
    underlay.layer.style.transform = `translate3d(${underlayOffsetX(value, window.innerWidth)}px, 0, 0)`;
    underlay.plate.style.transition = transition ? `transform ${transition}, box-shadow ${transition}` : 'none';
    underlay.plate.style.transform = transform;
    // 그림자는 화면 높이 전체인 바탕 판이 맡는다(본문이 짧아도 끝까지 경계가 보이게)
    underlay.plate.style.boxShadow = underlayEdgeShadow(value, window.innerWidth);
    if (underlay.footer) {
        underlay.footer.style.transition = transition ? `transform ${transition}` : 'none';
        underlay.footer.style.transform = transform;
    }
};

/** 레이어를 치우고 본문을 원래대로 둔다 — 복사본 노드는 보관소에 그대로 남는다 */
export const unmountUnderlay = (underlay: Underlay, content: HTMLElement | null): void => {
    underlay.layer.remove();
    underlay.plate.remove();
    underlay.node.remove();
    [content, underlay.footer].forEach((element: HTMLElement | null) => {
        if (!element) return;
        ['position', 'z-index', 'transform', 'transition'].forEach((name: string) => element.style.removeProperty(name));
        // 우리가 넣은 스타일만 있었으면 style 속성 자체를 지워 흔적을 남기지 않는다
        if (element.getAttribute('style') === '') element.removeAttribute('style');
    });
};

/** 앱 본문 바탕색 — body 계산값, 투명이면 토큰(#F5F8F7) */
const appBackground = (): string => {
    const value: string = getComputedStyle(document.body).backgroundColor;
    const transparent: boolean = value === '' || value === 'transparent' || /^rgba\(.*,\s*0\)$/.test(value);
    return transparent ? claudeTokens.color.bg : value;
};
